import { formatKoreanDate } from '@/lib/date';
import { parseFaqFromContent, splitTrailingPromo } from '@/lib/faq-parser';
import RelatedCourseArticles from '@/components/related-course-articles';
import { cn, fromSlug, getLast, isExternalHref, slugify } from '@/lib/utils';
import { getArticleTypeKorean } from '@/lib/utils.type';
import { useArticleDetail } from '@/services/query/article.query';
import { useStoryBanner } from '@/services/query/banner.query';
import { ArticleEntity, ArticleType } from '@/services/types/article.type';
import { BannerEntity } from '@/services/types/banner.type';
import ListBackSvg from '@/svg/list-back.svg';
import { BreadcrumbItemType } from '@/types/breadcrumb-item.type';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import type { ReactNode } from 'react';
import ImageOptimizer from './image-optimizer';
import PageHeader from './page-header';
import SafeHtmlRenderer from './safe-html-renderer';

const emptyRenderer = {
  renderHeader: () => null,
  renderFooter: () => null,
};

// 게시글 상세 상단(제목 아래) 배너. pc/mobile 각각 우선순위 최상위 1건을 노출
function ArticleTopBanner({
  pc,
  mobile,
}: {
  pc?: BannerEntity;
  mobile?: BannerEntity;
}) {
  const pcImageSrc = pc?.upfilePath || mobile?.upfilePath;
  const mobileImageSrc = mobile?.upfilePath || pc?.upfilePath;

  if (!pcImageSrc || !mobileImageSrc) return null;

  const href = pc?.href || mobile?.href;
  const alt = pc?.title || mobile?.title || '광고';

  const content = (
    <>
      <div className="desktop:block relative hidden aspect-[1236/176] w-full">
        <ImageOptimizer
          src={pcImageSrc}
          alt={alt}
          fill
          className="object-cover"
        />
      </div>
      <div className="desktop:hidden relative aspect-[722/140] w-full">
        <ImageOptimizer
          src={mobileImageSrc}
          alt={alt}
          fill
          className="object-cover"
        />
      </div>
    </>
  );

  const linkedContent = href ? (
    isExternalHref(href) ? (
      // 외부 광고 링크: 새 탭 + SEO상 광고 표시(sponsored)
      <a
        href={href}
        target="_blank"
        rel="sponsored noopener noreferrer"
        className="block"
      >
        {content}
      </a>
    ) : (
      // 내부(자사) 링크: 클라이언트 라우팅 + follow 유지
      <Link href={href} className="block">
        {content}
      </Link>
    )
  ) : (
    content
  );

  return (
    <aside className="desktop:mx-7 desktop:mt-5 mt-4 overflow-hidden rounded-md">
      {linkedContent}
    </aside>
  );
}

const blogRenderer = {
  renderHeader: (article: ArticleEntity) => (
    <>
      <div className="border-b-stroke-white border-t-level-2 desktop:border-t-4 desktop:px-7 desktop:py-8 desktop:flex-row desktop:items-center desktop:gap-2 flex w-full flex-col justify-between gap-2.5 border-t-2 border-b-1 pt-3 pb-4">
        <div className="flex items-center">
          {/* 글 제목은 페이지의 주제라 h1로 둔다(검색엔진이 제목을 식별). 모양은 그대로다. */}
          <h1 className="typo-18 text-level-3 desktop:typo-24 desktop:leading-snug leading-relaxed font-bold">
            {article.title}
          </h1>
        </div>
        <span className="typo-14 text-level-6 desktop:typo-20 shrink-0 font-semibold">
          {formatKoreanDate(article.viewDatetime)}
        </span>
      </div>
      {/* <div className="desktop:flex desktop:flex-col desktop:items-center desktop:mt-15">
        <div className="desktop:mb-15 desktop:gap-8 mb-14 flex w-full flex-col gap-4">
          <div className="flex gap-4">
            <div className="desktop:mx-auto desktop:w-[750px] relative aspect-[27/16] h-auto w-full overflow-hidden rounded-lg">
              <ImageOptimizer
                src={article.thumbPath}
                alt={article.title}
                fill
                className="object-cover"
              />
            </div>
          </div>
          {article.subTitle && (
            <div className="text-level-3 typo-18 desktop:typo-22 desktop:leading-snug desktop:mt-[14px] desktop:mx-auto desktop:w-[750px] text-center leading-snug font-semibold">
              {article.subTitle}
            </div>
          )}
          <div className="fle-col desktop:flex desktop:h-full desktop:grow desktop:flex-col desktop:justify-between text-center">
            <div className="text-level-6 typo-14 desktop:typo-20 font-semibold">
              {formatKoreanDate(article.viewDatetime)}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full pb-7">
        <PoliceLineDivider />
      </div> */}
    </>
  ),
  renderFooter: () => null,
};

const commonRenderer = {
  renderHeader: (article: ArticleEntity) => (
    <>
      <div className="desktop:mb-10 mx-auto mb-9 w-full">
        <h1 className="text-level-2 typo-28 desktop:typo-50 desktop:mb-2 leading-snug font-black">
          {getArticleTypeKorean(article.board?.type || ArticleType.NOTICE)}
        </h1>
      </div>
      {/* <div className="border-b-stroke-white border-t-level-2 desktop:border-t-4 desktop:px-7 desktop:py-8 flex w-full items-center justify-between gap-2 border-t-2 border-b-1 pt-6 pb-5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="typo-18 text-level-3 desktop:typo-24 truncate font-bold">
            {article.title}
          </span>
        </div>
        <span className="typo-14 text-level-6 desktop:typo-20 shrink-0 font-semibold">
          {formatKoreanDate(article.viewDatetime)}
        </span>
      </div> */}
      <div className="border-b-stroke-white border-t-level-2 desktop:border-t-4 desktop:px-7 desktop:py-8 desktop:flex-row desktop:items-center desktop:gap-2 flex w-full flex-col justify-between gap-2.5 border-t-2 border-b-1 pt-3 pb-4">
        <div className="flex items-center">
          <span className="typo-18 text-level-3 desktop:typo-24 desktop:leading-snug leading-relaxed font-bold">
            {article.title}
          </span>
        </div>
        <span className="typo-14 text-level-6 desktop:typo-20 shrink-0 font-semibold">
          {formatKoreanDate(article.viewDatetime)}
        </span>
      </div>
    </>
  ),
  renderFooter: (article: ArticleEntity, listHref: string) => (
    <>
      {/* 리스트 목록으로 가기 버튼 */}
      {listHref && (
        <Link
          href={listHref}
          className="desktop:my-7 mt-4 mb-4 flex items-center justify-end gap-2"
        >
          <span className="typo-16 text-font-blue desktop:typo-28 desktop:font-extrabold font-bold">
            목록으로
          </span>
          <ListBackSvg className="desktop:size-7 size-4 shrink-0" />
        </Link>
      )}
      {/* 이전글 다음글  */}
      <div className="divide-stroke-white flex w-full flex-col divide-y pb-15">
        {article.previous && (
          <Link
            href={`${listHref}/${slugify(article.previous.title, article.previous.id)}`}
          >
            <div className="border-stroke-white desktop:px-7 desktop:py-8 flex items-center justify-between gap-2 border-t pt-6 pb-5">
              <div className="flex min-w-0 items-center gap-3">
                <span className="typo-14 text-level-6 desktop:typo-24 desktop:font-semibold desktop:text-level-5 shrink-0 font-bold">
                  이전글
                </span>
                <span className="typo-18 text-level-3 desktop:typo-24 truncate font-bold">
                  {article.previous.title}
                </span>
              </div>
              <span className="typo-14 text-level-6 desktop:typo-20 shrink-0 font-semibold">
                {formatKoreanDate(article.previous.viewDatetime)}
              </span>
            </div>
          </Link>
        )}
        {article.next && (
          <Link
            href={`${listHref}/${slugify(article.next.title, article.next.id)}`}
          >
            <div className="border-stroke-white desktop:px-7 desktop:py-8 flex items-center justify-between gap-2 border-b pt-6 pb-5">
              <div className="flex min-w-0 items-center gap-3">
                <span className="typo-14 text-level-6 desktop:typo-24 desktop:font-semibold desktop:text-level-5 shrink-0 font-bold">
                  다음글
                </span>
                <span className="typo-18 text-level-3 desktop:typo-24 truncate font-bold">
                  {article.next.title}
                </span>
              </div>
              <span className="typo-14 text-level-6 desktop:typo-20 shrink-0 font-semibold">
                {formatKoreanDate(article.next.viewDatetime)}
              </span>
            </div>
          </Link>
        )}
      </div>
    </>
  ),
};

// 게시판별 렌더링 로직 분리
const renderers: Record<
  ArticleType,
  {
    renderHeader: (article: ArticleEntity) => ReactNode;
    renderFooter: (article: ArticleEntity, listHref: string) => ReactNode;
  }
> = {
  [ArticleType.BLOG]: blogRenderer,
  [ArticleType.LICENSE_PROCESS]: blogRenderer,
  [ArticleType.TEST_COURSE_ANALYSIS]: blogRenderer,
  [ArticleType.EVENT]: blogRenderer,
  [ArticleType.NOTICE]: commonRenderer,
  [ArticleType.STORY]: emptyRenderer,
  [ArticleType.FAQ]: emptyRenderer,
  [ArticleType.TEST_COURSE_ROAD_ANALYSIS]: blogRenderer,
  [ArticleType.TEST_COURSE_SKILL_ANALYSIS]: blogRenderer,
};

interface Props {
  slug?: string;
  breadcrumbItems: BreadcrumbItemType[];
  // 스토리 게시판 글 상세에서만 상단 배너를 노출
  showStoryBanner?: boolean;
  // 본문에서 FAQ 블록을 제거한다. FAQ는 서버(page.tsx)에서
  // light DOM + FAQPage(JSON-LD)로 별도 렌더되므로 중복을 막기 위함.
  extractFaq?: boolean;
  // 서버에서 렌더한 FAQ 섹션(FaqSection). 마무리 섹션과 말미 프로모션 블록
  // 사이에 배치하기 위해 노드로 주입받는다. SSR 유지를 위해 로딩 분기에서도 렌더.
  faqSlot?: ReactNode;
}

export default function ArticleDetail({
  slug,
  breadcrumbItems,
  showStoryBanner,
  extractFaq,
  faqSlot,
}: Props) {
  const id = fromSlug(slug ? slug : (useParams()?.slug as string));
  const { data, isLoading, error } = useArticleDetail(Number(id));
  const article: ArticleEntity | undefined = data?.data;

  // 글 로드 후, 해당 글/게시판에 타겟된 상단 배너 조회
  const { data: storyBannerData } = useStoryBanner(
    { articleId: article?.id, boardType: article?.board?.type },
    Boolean(showStoryBanner && article?.id),
  );
  const pcBanner = storyBannerData?.data?.pc?.[0];
  const mobileBanner = storyBannerData?.data?.mobile?.[0];
  const hasTitleAd = Boolean(pcBanner?.upfilePath || mobileBanner?.upfilePath);

  // 이전글 다음글 임시 테스트용
  // if (article) {
  //   article.prevArticle = JSON.parse(JSON.stringify(article));
  // }
  // if (article) {
  //   article.nextArticle = JSON.parse(JSON.stringify(article));
  // }

  const articleType = article?.board?.type;
  const selectedRenderer =
    articleType && articleType in renderers
      ? renderers[articleType as ArticleType]
      : commonRenderer;
  const { renderHeader, renderFooter } = selectedRenderer;

  // 본문(FAQ 제거본)을 마무리까지의 main과 말미 프로모션(배너 + "함께 보면 좋은 글")
  // trailing으로 분할한다. 프로모션이 없으면 trailing은 빈 문자열.
  const bodyHtml = article
    ? extractFaq
      ? parseFaqFromContent(article.content).contentWithoutFaq
      : article.content
    : '';
  const { main: bodyMain, trailing: bodyTrailing } = extractFaq
    ? splitTrailingPromo(bodyHtml)
    : { main: bodyHtml, trailing: '' };

  const lastBreadcrumb = getLast(breadcrumbItems);
  const lastHref = lastBreadcrumb?.href || '';
  const currentBreadcrumb = article
    ? [
        ...breadcrumbItems,
        {
          label: `${article?.title}`,
          href: `${lastHref + '/' + article?.id}`,
        },
      ]
    : breadcrumbItems;

  return (
    <main>
      <PageHeader breadcrumbItems={currentBreadcrumb} />

      {isLoading ? (
        // 본문은 클라이언트에서 로드되지만 FAQ는 초기 HTML(SSR)에 포함돼야 하므로,
        // 로딩 중에도 faqSlot을 렌더해 크롤러가 볼 수 있게 한다.
        faqSlot ? (
          <section className="desktop:flex desktop:justify-center">
            <div className="desktop:web-w desktop:w-full flex flex-col">
              {faqSlot}
            </div>
          </section>
        ) : (
          <></>
        )
      ) : error || !article ? (
        <div>게시글을 찾을 수 없습니다.</div>
      ) : (
        <section className="desktop:flex desktop:justify-center">
          <div className="desktop:web-w desktop:w-full flex flex-col">
            {renderHeader(article)}
            <ArticleTopBanner pc={pcBanner} mobile={mobileBanner} />
            <div className="desktop:flex desktop:justify-center">
              <SafeHtmlRenderer
                imageAltFallback={article.title}
                html={bodyMain}
                className={cn(
                  'desktop:max-w-[750px]',
                  hasTitleAd ? 'desktop:pt-6 pt-4' : 'desktop:pt-13 pt-7',
                )}
                // 코스분석 글: 본문 문단 폰트를 18px로(제목은 유지, FAQ와 통일)
                paragraphFontSizePx={extractFaq ? 18 : undefined}
              />
            </div>
            {/* FAQ: 마무리 섹션 다음, 말미 프로모션 앞에 배치 */}
            {faqSlot}
            {extractFaq && article.academyId ? (
              // 코스분석 글: 정적 트레일링(예약 배너 + 수작업 "함께 보면 좋은 글")은
              // 렌더하지 않고(splitTrailingPromo가 이미 bodyMain에서 분리함),
              // 같은 시험장(academy)의 다른 코스분석 글을 동적으로 노출한다.
              <RelatedCourseArticles
                academyId={article.academyId}
                currentId={article.id}
              />
            ) : bodyTrailing ? (
              <div className="desktop:mt-16 desktop:flex desktop:justify-center mt-12">
                <SafeHtmlRenderer
                  imageAltFallback={article.title}
                  html={bodyTrailing}
                  className="desktop:max-w-[750px] w-full"
                  paragraphFontSizePx={18}
                />
              </div>
            ) : null}
            {renderFooter(article, lastHref)}
          </div>
        </section>
      )}
    </main>
  );
}
